import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Button,
  Input,
  FormLabel,
  Checkbox,
  Spacer,
  Text,
  VStack,
  Select,
} from "@chakra-ui/react";
import { useState } from "react";
import { useGlobalContext } from "../Layout/GlobalState";
import { OfiDateTimePicker } from "../HelperComponents/DateTimePickers";
import {
  timeInMsToDateTimeString,
  timeInMsToString,
} from "../Services/ResultService";
import { Gender, Startgroup } from "../api/Dtos";

export function hasChanges<T>(oldObj: T, updatedObj: T): boolean {
  // Compare each key in the old object
  for (const key in oldObj) {
    if (oldObj[key] !== updatedObj[key]) {
      return true; // Found a change
    }
  }

  // Compare each key in the updated object in case there are extra properties
  for (const key in updatedObj) {
    if (oldObj[key] !== updatedObj[key]) {
      return true; // Found a change
    }
  }

  return false; // No changes found
}

export function ModifyModal<T>(props: {
  target: T;
  displayEditableNames?: Array<string>;
  editableFields: Array<keyof T>;
  displayFixedNames?: Array<string>;
  fixedFields: Array<keyof T>;
  onSubmit: (updated: T, old: T) => Promise<boolean>;
  onClose: () => void;
  size?: string;
  forceDisplayToTypeMap?: Map<string, string>;
}) {
  const [formState, setFormState] = useState<T>({ ...props.target });
  const globalContext = useGlobalContext();

  const getType = (displayName: string, value: T[keyof T]) => {
    const fieldType = typeof value;
    if (props.forceDisplayToTypeMap) {
      return props.forceDisplayToTypeMap.get(displayName) ?? fieldType;
    } else {
      return fieldType;
    }
  };

  const handleChange = (field: keyof T, value: any) => {
    console.log("handlachange", field, value, formState);
    setFormState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    const isSuccess = await props.onSubmit(formState, props.target);
    if (isSuccess) {
      props.onClose();
    }
  };

  return (
    <Modal
      closeOnOverlayClick={true}
      scrollBehavior={"inside"}
      isOpen={true}
      size={props.size ?? "xl"}
      onClose={() => {
        props.onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent
        background={"white"}
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <ModalHeader>Ändra</ModalHeader>
        <ModalCloseButton />
        <ModalBody scrollBehavior={"auto"} margin={"20px"}>
          <form onSubmit={(e) => e.preventDefault()}>
            <VStack spacing={"4"} align="left">
              {props.fixedFields.map((field, index) => {
                const value = formState[field];
                const displayName = props.displayFixedNames
                  ? props.displayFixedNames[index] ?? field
                  : field;
                const fieldType = getType(displayName as string, value);

                return (
                  <div key={String(field)}>
                    <HStack align={"left"}>
                      <Text fontWeight={"bold"}>{String(displayName)}:</Text>
                      {fieldType === "string" || fieldType === "number" ? (
                        <Text>{value as unknown as string}</Text>
                      ) : fieldType === "boolean" ? (
                        <Checkbox
                          isChecked={value as unknown as boolean}
                          marginLeft={"10px"}
                          onChange={() => {}}
                        />
                      ) : fieldType === "Date" ? (
                        <Text>
                          {timeInMsToDateTimeString(
                            (value as unknown as Date | undefined)?.getDate()
                          )}
                        </Text>
                      ) : null}
                    </HStack>
                  </div>
                );
              })}
              {props.editableFields.map((field, index) => {
                const value = formState[field];
                const displayName = props.displayEditableNames
                  ? props.displayEditableNames[index] ?? field
                  : field;
                const fieldType = getType(displayName as string, value);
                console.log("getType", field, value, fieldType);
                return (
                  <div key={String(field)}>
                    <FormLabel fontWeight={"bold"}>
                      {String(displayName)}:
                      {displayName === "Klass" ? (
                        <Select
                          minW={100}
                          value={value as any}
                          onChange={(e) => {
                            handleChange(field, e.target.value);
                          }}
                        >
                          {globalContext.competition?.disciplines.map((c) => {
                            return (
                              <option key={c.id} value={c.name}>
                                {c.name}
                              </option>
                            );
                          })}
                        </Select>
                      ) : (field as string) === "gender" ? (
                        <Select
                          minW={100}
                          value={value == null ? "null" : String(value)}
                          onChange={(e) => {
                            handleChange(
                              field,
                              e.target.value === "null"
                                ? null
                                : (e.target.value as Gender)
                            );
                          }}
                        >
                          <option key={"man"} value={"MAN"}>
                            man
                          </option>
                          <option key={"woman"} value={"WOMAN"}>
                            kvinna
                          </option>
                          {fieldType.includes("null") && (
                            <option key={"null"} value={"null"}>
                              -
                            </option>
                        )}
                        </Select>
                      ) : (field as string) === "startgroupId" ? (
                        <StartgroupPicker
                          value={value == null ? undefined : (value as number)}
                          onChange={(startgroup) => {
                            handleChange(field, startgroup?.id ?? null);
                          }}
                        />
                      ) : fieldType === "string" || fieldType === "number" ? (
                        <Input
                          type={fieldType === "string" ? "text" : "number"}
                          value={value as any}
                          onChange={(e) =>
                            handleChange(
                              field,
                              fieldType === "number"
                                ? +e.target.value
                                : e.target.value
                            )
                          }
                        />
                      ) : fieldType === "boolean" ? (
                        <Checkbox
                          isChecked={value as unknown as boolean}
                          marginLeft={"10px"}
                          onChange={(e) =>
                            handleChange(field, e.target.checked)
                          }
                        />
                      ) : fieldType === "Date" ? (
                        <OfiDateTimePicker
                          startDate={value as unknown as Date | undefined}
                          endDate={undefined}
                          selectsStart={true}
                          selected={value as unknown as Date | undefined}
                          onChange={(newDate) => handleChange(field, newDate)}
                          earliestPossibleDate={new Date(0)}
                        />
                      ) : null}
                    </FormLabel>
                  </div>
                );
              })}
            </VStack>
          </form>
        </ModalBody>
        <ModalFooter borderRadius={"0px"} background={"blue.100"}>
          <HStack width={"100%"}>
            <Button type="button" onClick={props.onClose}>
              Tillbaka
            </Button>
            <Spacer />
            <Button
              type="button"
              onClick={() => handleSubmit()}
              colorScheme="green"
            >
              Spara
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function StartgroupPicker(props: {
  value: number | undefined;
  onChange: (startgroup: Startgroup | null) => void;
}) {
  const globalContext = useGlobalContext();

  return (
    <Select
      marginRight={"40px"}
      minW={100}
      maxW={250}
      placeholder={"-"}
      value={props.value}
      onChange={(event) => {
        const id = parseInt(event.target?.value);
        props.onChange(
          globalContext.competition?.startgroups.find((s) => s.id === id) ??
            null
        );
      }}
    >
      {globalContext.competition?.startgroups.map((s) => {
        return (
          <option key={s.id} value={s.id} selected={s.id === props.value}>
            {timeInMsToString(s.starttime_planned)}
          </option>
        );
      })}
    </Select>
  );
}
